import React from "react";
import { CredentialConfig, PropsAreEqual } from "./types";
import { useCredentials } from "./useCredentials";

export const withCredentials = <
  P extends { value: any; credentialConfig?: CredentialConfig }
>(
  component: {
    (props: P): Exclude<React.ReactNode, undefined>;
    displayName?: string;
  },
  credentialConfig?: CredentialConfig,
  propsAreEqual?: PropsAreEqual<P> | false,
  componentName = component.displayName ?? component.name
): {
  (props: P): JSX.Element | null;
  displayName: string;
} => {
  function withCredentials(props: P) {
    const credentials = useCredentials();
    if (typeof credentials === "undefined") {
      throw new Error(
        "withCredentials must be used within CredentialContextProvider"
      );
    }

    const { hasCredential } = credentials!;

    const credConfig = credentialConfig ?? props.credentialConfig;

    if (
      typeof credConfig === "undefined" ||
      !credConfig.hasOwnProperty("name") ||
      !credConfig.hasOwnProperty("objectType")
    ) {
      throw new Error(
        `You used withCredentials without providing credential config. Please provide credential config as a second argument to withCredential HOC or as credentialConfig component prop to component ${componentName}.`
      );
    }

    if (!hasCredential(credConfig.name, credConfig.objectType, "show")) {
      return null;
    }

    return component({
      ...props,
      isEditable: hasCredential(credConfig.name, credConfig.objectType, "edit"),
    }) as JSX.Element;
  }

  withCredentials.displayName = `withCredentials(${componentName})`;

  const wrappedComponent =
    propsAreEqual === false
      ? withCredentials
      : React.memo(withCredentials, propsAreEqual);

  // copyStaticProperties(component, wrappedComponent);

  return wrappedComponent as typeof withCredentials;
};
